import React, { useState, useEffect } from 'react';
import { calendarAPI } from '../../lib/api';
import { cn, formatDate, formatDateTime } from '../../lib/utils';
import { toast } from 'sonner';
import {
  Bell,
  AlertTriangle,
  Clock,
  CheckCircle2,
  ChevronRight,
  Loader2,
  Calendar,
  AlertCircle,
  Sparkles,
  RefreshCcw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';

const PRIORITY_COLORS = {
  urgent: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  normal: 'bg-blue-100 text-blue-700 border-blue-200',
  low: 'bg-slate-100 text-slate-600 border-slate-200',
};

const PRIORITY_BADGES = {
  urgent: 'destructive',
  high: 'warning',
  normal: 'default',
  low: 'secondary',
};

export const SmartReminders = ({ onEventClick, onRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [reminders, setReminders] = useState(null);
  const [generatingReminders, setGeneratingReminders] = useState({});

  useEffect(() => {
    fetchReminders();
  }, []);

  const fetchReminders = async () => {
    setLoading(true);
    try {
      const response = await calendarAPI.getSmartReminders(14, true);
      setReminders(response.data);
    } catch (error) {
      console.error('Failed to fetch reminders:', error);
      toast.error('Failed to load reminders');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReminders = async (eventId, eventTitle) => {
    setGeneratingReminders(prev => ({ ...prev, [eventId]: true }));
    try {
      const response = await calendarAPI.generateReminders(eventId, 'auto');
      toast.success(`Created ${response.data.reminders_created} reminders for "${eventTitle}"`);
      fetchReminders();
      if (onRefresh) onRefresh();
    } catch (error) {
      toast.error('Failed to generate reminders');
    } finally {
      setGeneratingReminders(prev => ({ ...prev, [eventId]: false }));
    }
  };

  const ReminderItem = ({ reminder }) => (
    <div 
      className={cn(
        "p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow",
        PRIORITY_COLORS[reminder.priority] || PRIORITY_COLORS.normal
      )}
      onClick={() => onEventClick && reminder.event_id && onEventClick(reminder.event_id)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{reminder.title}</p>
          <p className="text-xs opacity-70 mt-0.5">{reminder.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <Clock className="h-3 w-3" />
            <span className="text-xs">
              {formatDateTime(reminder.due_date)}
            </span>
          </div>
        </div>
        <Badge variant={PRIORITY_BADGES[reminder.priority] || 'default'} className="shrink-0">
          {reminder.priority}
        </Badge>
      </div>
    </div>
  );

  const SuggestionItem = ({ suggestion }) => (
    <div className="p-3 rounded-lg border border-amber-200 bg-amber-50">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <p className="font-medium text-sm text-amber-800">{suggestion.event_title}</p>
          </div>
          <p className="text-xs text-amber-700 mt-1">{suggestion.message}</p>
          <p className="text-xs text-amber-600 mt-1">
            Event Date: {formatDate(suggestion.event_date)}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 border-amber-300 text-amber-700 hover:bg-amber-100"
          disabled={generatingReminders[suggestion.event_id]}
          onClick={(e) => {
            e.stopPropagation();
            handleGenerateReminders(suggestion.event_id, suggestion.event_title);
          }}
        >
          {generatingReminders[suggestion.event_id] ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Sparkles className="h-3 w-3 mr-1" />
              Generate
            </>
          )}
        </Button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!reminders) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-slate-500">
          Unable to load reminders
        </CardContent>
      </Card>
    );
  }

  const { overdue, urgent, high_priority, normal, suggestions, summary } = reminders;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bell className="h-5 w-5 text-primary" />
            <CardTitle>Smart Reminders</CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchReminders}>
            <RefreshCcw className="h-4 w-4" />
          </Button>
        </div>
        <CardDescription>
          Intelligent reminder system with escalation
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="text-center p-2 rounded-lg bg-red-50 border border-red-200">
            <p className="text-xl font-bold text-red-600">{summary.overdue_count}</p>
            <p className="text-xs text-red-600">Overdue</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-orange-50 border border-orange-200">
            <p className="text-xl font-bold text-orange-600">{summary.urgent_count}</p>
            <p className="text-xs text-orange-600">Urgent</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-amber-50 border border-amber-200">
            <p className="text-xl font-bold text-amber-600">{summary.high_priority_count}</p>
            <p className="text-xs text-amber-600">High</p>
          </div>
          <div className="text-center p-2 rounded-lg bg-blue-50 border border-blue-200">
            <p className="text-xl font-bold text-blue-600">{summary.normal_count}</p>
            <p className="text-xs text-blue-600">Normal</p>
          </div>
        </div>

        <ScrollArea className="h-[400px]">
          <Accordion type="multiple" defaultValue={['overdue', 'urgent', 'suggestions']} className="space-y-2">
            {/* Overdue */}
            {overdue.length > 0 && (
              <AccordionItem value="overdue" className="border rounded-lg">
                <AccordionTrigger className="px-3 py-2 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <span className="font-medium">Overdue ({overdue.length})</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  <div className="space-y-2">
                    {overdue.map(reminder => (
                      <ReminderItem key={reminder.reminder_id} reminder={reminder} />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* Urgent */}
            {urgent.length > 0 && (
              <AccordionItem value="urgent" className="border rounded-lg">
                <AccordionTrigger className="px-3 py-2 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-orange-500" />
                    <span className="font-medium">Urgent ({urgent.length})</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  <div className="space-y-2">
                    {urgent.map(reminder => (
                      <ReminderItem key={reminder.reminder_id} reminder={reminder} />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* Suggestions */}
            {suggestions.length > 0 && (
              <AccordionItem value="suggestions" className="border rounded-lg border-amber-200">
                <AccordionTrigger className="px-3 py-2 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    <span className="font-medium text-amber-700">
                      Events Needing Reminders ({suggestions.length})
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  <div className="space-y-2">
                    {suggestions.map(suggestion => (
                      <SuggestionItem key={suggestion.event_id} suggestion={suggestion} />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* High Priority */}
            {high_priority.length > 0 && (
              <AccordionItem value="high" className="border rounded-lg">
                <AccordionTrigger className="px-3 py-2 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-amber-500" />
                    <span className="font-medium">High Priority ({high_priority.length})</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  <div className="space-y-2">
                    {high_priority.map(reminder => (
                      <ReminderItem key={reminder.reminder_id} reminder={reminder} />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* Normal */}
            {normal.length > 0 && (
              <AccordionItem value="normal" className="border rounded-lg">
                <AccordionTrigger className="px-3 py-2 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Bell className="h-4 w-4 text-blue-500" />
                    <span className="font-medium">Upcoming ({normal.length})</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-3 pb-3">
                  <div className="space-y-2">
                    {normal.map(reminder => (
                      <ReminderItem key={reminder.reminder_id} reminder={reminder} />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>

          {/* Empty State */}
          {overdue.length === 0 && urgent.length === 0 && high_priority.length === 0 && normal.length === 0 && suggestions.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500" />
              <p className="font-medium">All caught up!</p>
              <p className="text-sm">No pending reminders</p>
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
};

export default SmartReminders;
